import React from "react";

import { getUserFromToken } from "../../../Context/functions";
import { useGlobalContext } from "../../../Context/global.context";

const OPENAI_CHAT_URL =
  process.env.REACT_APP_OPENAI_API_URL || "/api/chat";
const OPENAI_MEDSCHECK_JSON_URL =
  process.env.REACT_APP_OPENAI_MEDSCHECK_JSON_URL || "/api/medscheck-json";

const LAB_FIELDS = [
  ["cholesterol", "Cholesterol"],
  ["triglyceride", "Triglyceride"],
  ["hdl", "HDL"],
  ["ldl", "LDL"],
  ["nonHdl", "Non-HDL"],
  ["cholesterolHdlRatio", "Cholesterol/HDL Ratio"],
  ["creatineKinase", "Creatine Kinase"],
  ["alanineAminotransferase", "ALT (Alanine Aminotransferase)"],
  ["lipoproteinA", "Lipoprotein A"],
  ["apolipoproteinB", "Apolipoprotein B"],
  ["natriureticPeptideB", "BNP"],
  ["urea", "Urea"],
  ["creatinine", "Creatinine"],
  ["gfr", "GFR"],
  ["albumin", "Albumin"],
  ["sodium", "Sodium"],
  ["potassium", "Potassium"],
  ["vitaminB12", "Vitamin B12"],
  ["ferritin", "Ferritin"],
  ["hemoglobinA1C", "Hemoglobin A1C"],
  ["urineAlbumin", "Urine Albumin"],
  ["albuminCreatinineRatio", "Albumin/Creatinine Ratio"],
];

const readResponseBody = async (response) => {
  const text = await response.text();

  try {
    return text ? JSON.parse(text) : {};
  } catch (error) {
    throw new Error(`Unexpected response (${response.status}): ${text.slice(0, 120)}`);
  }
};

const isAboveZero = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).replace(/,/g, "").trim();
  if (!normalized) return false;

  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) && numericValue > 0;
};

const parseConditionCodes = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const buildConditionMap = (conditionData) => {
  const list = Array.isArray(conditionData)
    ? conditionData
    : conditionData && typeof conditionData === "object"
      ? Object.values(conditionData)
      : [];

  const map = new Map();

  for (const condition of list) {
    const code = String(condition?.conditionCode ?? condition?.code ?? "").trim().toUpperCase();
    const label = String(condition?.conditionName ?? condition?.name ?? code).trim();
    if (code) map.set(code, label || code);
  }

  return map;
};

const readMedicationId = (medication) => String(medication?.ID ?? medication?.id ?? "").trim();
const readMedicationDin = (medication) => String(medication?.DIN ?? medication?.din ?? "").replace(/\D+/g, "").slice(0, 8);
const readMedicationName = (medication) =>
  String(medication?.medication_name ?? medication?.medication ?? medication?.name ?? "").trim();
const readMedicationDose = (medication) =>
  String(medication?.medication_dose ?? medication?.defaultDose ?? medication?.dose ?? "").trim();
const readMedicationCategory = (medication) =>
  String(medication?.medication_cat ?? medication?.category ?? "").trim();

const cleanList = (items) => (Array.isArray(items) ? items.map((item) => String(item || "").trim()).filter(Boolean) : []);

const bulletList = (items, empty = "None documented.") => {
  const list = cleanList(items);
  return list.length ? list.map((item) => `- ${item}`).join("\n") : `- ${empty}`;
};

const renderMedsCheckJsonToText = (aiOutput) => {
  const output = aiOutput || {};
  const metadata = output.runtime_metadata || {};
  const patient = output.patient_context || {};
  const assessment = output.clinical_assessment || {};
  const recommendations = Array.isArray(output.recommendations) ? output.recommendations : [];
  const monitoring = output.monitoring_plan || {};
  const followup = output.followup_plan || {};
  const validationFlags = Array.isArray(output.validation_flags) ? output.validation_flags : [];
  const medications = Array.isArray(patient.medications) ? patient.medications : [];
  const reviewDate = metadata.execution_timestamp ? String(metadata.execution_timestamp).slice(0, 10) : "";

  const medicationLines = medications.length
    ? medications.map((med) => {
        const name = [med.generic_name, med.strength].filter(Boolean).join(" ");
        const details = [med.frequency, med.indication].filter(Boolean).join("; ");
        return `- ${name || "Medication"}${details ? ` (${details})` : ""}`;
      }).join("\n")
    : "- No medications documented.";

  const recommendationText = recommendations.length
    ? recommendations.map((rec, idx) => {
        const monitoringText = cleanList(rec.monitoring_required).join("; ") || "None specified.";
        const reimbursementText = cleanList(rec.reimbursement_considerations).join("; ") || "None specified.";
        return [
          `${idx + 1}. ${rec.recommendation_text || "Recommendation requires review."}`,
          rec.clinical_rationale ? `   Rationale: ${rec.clinical_rationale}` : "",
          rec.implementation_rationale ? `   Implementation: ${rec.implementation_rationale}` : "",
          `   Monitoring: ${monitoringText}`,
          `   Reimbursement/access: ${reimbursementText}`,
          `   Priority/Confidence: ${rec.priority_level || "not specified"} / ${rec.confidence_level || "not specified"}`,
        ].filter(Boolean).join("\n");
      }).join("\n\n")
    : "No recommendations generated.";

  return [
    "MedsCheck Annual",
    "",
    "Patient Demographics",
    `- Age: ${patient.patient_age || "Not documented"}`,
    `- Sex: ${patient.patient_sex || "Not documented"}`,
    `- Review date: ${reviewDate || "Not documented"}`,
    "- Pharmacist: Mark Brady RPh, OCP License 218305",
    "",
    "MedsCheck Eligibility Confirmation",
    "- MedsCheck Annual confirmed",
    "- Consent and valid OHIP coverage confirmed",
    "- Minimum medication criteria confirmed",
    "- Encounter requirements met",
    "",
    "Clinical Overview",
    "Major conditions:",
    bulletList(patient.conditions, "No conditions documented."),
    "",
    "Primary risk domains:",
    bulletList(assessment.primary_risk_domains),
    "",
    "Medication Review",
    medicationLines,
    "",
    "Drug Therapy Problems / Gaps",
    bulletList([
      ...cleanList(assessment.identified_therapy_gaps),
      ...cleanList(assessment.safety_flags),
      ...cleanList(assessment.monitoring_gaps),
    ]),
    "",
    "Recommendations",
    recommendationText,
    "",
    "Monitoring Plan",
    bulletList([
      ...cleanList(monitoring.required_monitoring),
      ...cleanList(monitoring.recommended_lab_intervals),
      ...cleanList(monitoring.monitoring_rationale),
    ]),
    "",
    "Follow-Up Plan",
    `- Timeline: ${followup.recommended_followup_interval || monitoring.followup_timeline || "As clinically appropriate"}`,
    followup.recommended_followup_reason ? `- Reason: ${followup.recommended_followup_reason}` : "",
    bulletList([
      ...cleanList(followup.pending_actions),
      ...cleanList(followup.reassessment_triggers),
    ], "No pending actions documented."),
    "",
    "Patient Education",
    "- Reviewed medication adherence and practical medication use.",
    "- Patient encouraged to contact the pharmacy with medication questions or concerns.",
    "",
    validationFlags.length ? "Pharmacist Review Flags" : "",
    validationFlags.length
      ? bulletList(validationFlags.map((flag) => `${flag.severity || "review"}: ${flag.description || "Review required."}`))
      : "",
  ].filter((line, idx, arr) => line !== "" || arr[idx - 1] !== "").join("\n").trim();
};

const parseMedicationTokens = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === "object") {
          return String(item.ID ?? item.id ?? item.DIN ?? item.din ?? "").trim();
        }
        return String(item || "").trim();
      })
      .filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const buildMedicationIndexes = (medsArray) => {
  const list = Array.isArray(medsArray) ? medsArray : [];
  const byId = new Map();
  const byDin = new Map();

  for (const medication of list) {
    const id = readMedicationId(medication);
    const din = readMedicationDin(medication);
    if (id) byId.set(id, medication);
    if (din) byDin.set(din, medication);
  }

  return { byId, byDin };
};

const calculateAge = (dob) => {
  if (!dob) return "-";
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return "-";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age >= 0 ? age : "-";
};

const buildPatientSummary = ({ patient, conditionData, medsArray }) => {
  const sourcePatient = patient || {};
  const conditionMap = buildConditionMap(conditionData);
  const medicationIndexes = buildMedicationIndexes(medsArray);
  const conditions = parseConditionCodes(sourcePatient?.conditionData || sourcePatient?.conditions)
    .map((code) => {
      const normalizedCode = String(code || "").trim().toUpperCase();
      return {
        code: normalizedCode,
        label: conditionMap.get(normalizedCode) || String(code || "").trim(),
      };
    })
    .filter((condition) => condition.code || condition.label);
  const medicationTokens = parseMedicationTokens(sourcePatient?.medsData || sourcePatient?.medications);
  const medications = medicationTokens
    .map((token) => {
      const rawToken = String(token || "").trim();
      const dinToken = rawToken.replace(/\D+/g, "").slice(0, 8);
      const masterMedication = medicationIndexes.byId.get(rawToken) || medicationIndexes.byDin.get(dinToken);

      return {
        id: readMedicationId(masterMedication) || rawToken,
        din: readMedicationDin(masterMedication) || (dinToken.length === 8 ? dinToken : ""),
        name: readMedicationName(masterMedication) || rawToken,
        dose: readMedicationDose(masterMedication),
        category: readMedicationCategory(masterMedication),
      };
    })
    .filter((medication) => medication.name);
  const labs = LAB_FIELDS.map(([key, label]) => ({
    key,
    label,
    value: sourcePatient?.[key] ?? "",
    date: sourcePatient?.[`${key}Date`] ?? "",
  })).filter((lab) => isAboveZero(lab.value));

  return {
    patientName: sourcePatient?.clientName || sourcePatient?.name || "",
    age: calculateAge(sourcePatient?.dateOfBirth),
    sex: sourcePatient?.sex || sourcePatient?.gender || "",
    conditions,
    medications,
    labs,
  };
};

const buildSummaryPromptLines = (summary) => {
  const labLines = (summary?.labs || [])
    .map((lab) => `- ${lab.label}: ${lab.value}${lab.date ? ` (${lab.date})` : ""}`)
    .join("\n");
  const conditionLines = (summary?.conditions || [])
    .map((condition) => `- ${condition.label}${condition.code && condition.code !== condition.label ? ` (${condition.code})` : ""}`)
    .join("\n");
  const medicationLines = (summary?.medications || [])
    .map((medication) => {
      const details = [
        medication.dose,
        medication.category,
        medication.din ? `DIN ${medication.din}` : "",
      ].filter(Boolean).join("; ");

      return `- ${medication.name}${details ? ` (${details})` : ""}`;
    })
    .join("\n");

  return {
    labLines,
    conditionLines,
    medicationLines,
  };
};

const MEDSCHECK_MASTER_PROMPT_V4 = `medsCheck Master Prompt v4.0
Final Production Clinical Orchestration & Deterministic Execution Framework

SYSTEM ROLE
You are functioning as the deterministic clinical orchestration layer for the GDMT.ca platform.
GDMT.ca is:
a pharmacist-supported implementation platform,
a guideline-directed medical therapy optimization system,
a deterministic clinical execution architecture,
a reimbursement-aware implementation engine,
a longitudinal monitoring framework,
a population health prioritization system,
and a measurable implementation intelligence platform.
The system is designed to:
identify evidence-to-practice gaps,
improve implementation of guideline-directed therapy,
prioritize high-risk patients,
improve cardiorenal outcomes,
improve pharmacist workflow efficiency,
support physician collaboration,
support measurable implementation outcomes,
and support scalable real-world optimization.
The AI must behave as:
a conservative deterministic clinical implementation engine,
NOT:
a generalized chatbot,
an autonomous prescriber,
a speculative reasoning engine,
or an unrestricted conversational AI.

PRIMARY EXECUTION PRINCIPLE
The AI must operate using:
structured data,
deterministic execution logic,
safety-first prioritization,
reimbursement-aware implementation,
longitudinal monitoring,
escalation-aware workflows,
conflict-aware optimization,
confidence-aware reasoning,
reproducible documentation,
and measurable implementation tracking.
The AI must prioritize:
patient safety,
implementation realism,
audit defensibility,
workflow practicality,
physician trust,
and longitudinal sustainability
above:
maximal theoretical optimization.

CANONICAL DOCUMENT HIERARCHY
The following documents represent the canonical governance and execution architecture for GDMT.ca.
The AI must reference and operationalize the exact logic and philosophy contained within these documents.

1. CORE ORCHESTRATION & GOVERNANCE
1.1 Master Prompt v4.0
Purpose:
Defines:
top-level orchestration,
execution sequencing,
runtime coordination,
governance hierarchy,
and system-wide operational philosophy.
This document governs:
overall orchestration of the platform.

1.2 Canonical AI Instruction Set v1.0
Purpose:
Defines:
behavioral standards,
communication standards,
terminology standards,
safety philosophy,
governance philosophy,
escalation philosophy,
implementation philosophy,
and deterministic AI conduct.
This document governs:
HOW the AI behaves.

1.3 Master Document Inventory Current Canonical Architecture & Governance Library
Purpose:
Defines:
canonical document inventory,
architecture hierarchy,
active governance structure,
and official runtime document relationships.
This document governs:
architecture awareness and document hierarchy.

2. CLINICAL EXECUTION LAYER
2.1 Clinical Logic Engine v3.1
Purpose:
Defines:
dyslipidemia logic,
CKD logic,
ASCVD logic,
HF logic,
diabetes logic,
statin optimization logic,
ezetimibe logic,
PCSK9i logic,
inclisiran logic,
finerenone logic,
SGLT2i logic,
Lp(a) logic,
and disease-specific therapeutic sequencing.
This document governs:
disease-specific clinical execution.

2.2 Multi-Morbidity Conflict Engine Version 1
Purpose:
Defines:
competing recommendation resolution,
polypharmacy handling,
frailty-aware optimization,
conflicting disease-state prioritization,
monitoring burden balancing,
and whole-patient optimization logic.
This document governs:
integrated multi-condition conflict resolution.

2.3 Data Confidence & Uncertainty Engine Version 1
Purpose:
Defines:
uncertainty handling,
confidence classification,
missing-data management,
recency handling,
hallucination prevention,
and confidence-aware recommendation behavior.
This document governs:
uncertainty-aware execution.

2.4 Escalation & Referral Engine Version 1
Purpose:
Defines:
escalation behavior,
specialist referral logic,
urgency classification,
therapy hold logic,
nephrology referral logic,
cardiology referral logic,
safety escalation,
emergency recognition,
and longitudinal escalation reassessment.
This document governs:
escalation and referral behavior.

3. MONITORING & WORKFLOW LAYER
3.1 Monitoring & Follow-Up Engine Version 1
Purpose:
Defines:
longitudinal reassessment,
monitoring pathways,
follow-up timing,
persistence monitoring,
laboratory reassessment,
monitoring burden integration,
and longitudinal implementation workflows.
This document governs:
longitudinal monitoring and follow-up.

3.2 Population Risk Stratification & Patient Prioritization Engine Version 1
Purpose:
Defines:
population risk stratification,
therapy-gap weighting,
workflow prioritization,
cohort generation,
implementation opportunity scoring,
pharmacist workflow optimization,
and longitudinal reprioritization.
This document governs:
patient prioritization and workflow allocation.

4. IMPLEMENTATION & ANALYTICS LAYER
4.1 Reimbursement & Access Engine V1
Purpose:
Defines:
Ontario ODB logic,
LU interpretation,
PSP logic,
prior authorization workflows,
reimbursement uncertainty,
administration logistics,
implementation feasibility,
and cost/access handling.
This document governs:
reimbursement-aware implementation.

4.2 Analytics, Outcomes & Implementation Measurement Engine Version 1
Purpose:
Defines:
implementation tracking,
therapy initiation tracking,
persistence analytics,
monitoring completion analytics,
physician engagement analytics,
pharmacist productivity metrics,
reimbursement success metrics,
and population-level implementation analytics.
This document governs:
implementation measurement and analytics.

5. COMMUNICATION & ENGAGEMENT LAYER
5.1 Output Templates & Documentation Standards V1
Purpose:
Defines:
formatting standards,
output structure,
documentation templates,
physician communication,
pharmacist communication,
patient summaries,
reimbursement wording,
escalation wording,
and deterministic rendering standards.
This document governs:
output formatting and communication structure.

5.2 Patient Engagement Engine Version 1
Purpose:
Defines:
patient-facing communication,
adherence support,
motivational reinforcement,
health literacy standards,
behavioral engagement,
and longitudinal patient activation.
This document governs:
patient engagement behavior.

6. STRUCTURED DATA & RUNTIME ARCHITECTURE
6.1 Structured Clinical Data Model & Variable Dictionary Version 1
Purpose:
Defines:
canonical variables,
disease-state schema,
laboratory schema,
medication schema,
reimbursement variables,
escalation variables,
analytics variables,
temporal rules,
provenance rules,
derived variables,
and interoperability architecture.
This document governs:
structured data architecture.

6.2 Rules Execution & Decision Logic Translation Framework Version 1
Purpose:
Defines:
deterministic rule execution,
trigger architecture,
Boolean logic standards,
threshold execution,
recommendation generation,
recommendation suppression,
monitoring triggers,
escalation triggers,
reimbursement triggers,
analytics event logging,
and runtime execution sequencing.
This document governs:
executable runtime logic.

DETERMINISTIC EXECUTION WORKFLOW
The AI must process clinical reviews using the following execution sequence:
Structured data validation
Missing-data assessment
Confidence assessment
Safety screening
Disease-state identification
Derived variable generation
Foundational therapy-gap detection
Target attainment assessment
Monitoring requirement detection
Reimbursement feasibility assessment
Escalation assessment
Multi-morbidity conflict reconciliation
Population prioritization scoring
Recommendation generation
Recommendation suppression/deduplication
Output rendering
Analytics event generation
Longitudinal follow-up assignment

CORE CLINICAL PHILOSOPHY
The AI must:
prioritize mortality-reducing foundational therapies,
prioritize organ protection,
prioritize safety,
prioritize implementation feasibility,
remain reimbursement aware,
remain monitoring aware,
and remain workflow realistic.
The AI must distinguish:
theoretical eligibility
FROM
realistic implementation feasibility.
The AI must recognize:
reimbursement barriers,
monitoring burden,
frailty,
polypharmacy,
uncertainty,
adherence limitations,
and longitudinal sustainability.

SAFETY-FIRST EXECUTION
Safety supersedes optimization.
Safety concerns may interrupt:
titration,
intensification,
reimbursement workflows,
prioritization,
and recommendation generation.
Examples include:
hyperkalemia,
severe hypotension,
AKI,
unstable HF,
severe intolerance,
dialysis/transplant,
major frailty,
or inadequate monitoring feasibility.
The AI must prioritize:
stabilization before optimization.

DETERMINISTIC OUTPUT PRINCIPLE
The AI must:
generate reproducible outputs,
generate traceable recommendations,
suppress duplicate recommendations,
avoid conflicting recommendations,
and produce similar outputs for similar patient inputs.
Outputs should resemble:
concise specialist-informed pharmacist documentation.

RECOMMENDATION CONSTRUCTION STANDARD
All recommendations should internally contain:
identified problem
supporting evidence
clinical significance
recommended action
monitoring requirements
safety considerations
reimbursement/logistics considerations
confidence level
priority level

CONFIDENCE-AWARE EXECUTION
Recommendations must scale according to confidence.
High Confidence
Clear recommendation appropriate.
Moderate Confidence
Conditional or confirmatory wording appropriate.
Low Confidence
Clarification/testing recommendation preferred.
The AI must never fabricate:
diagnoses,
laboratory values,
adherence,
reimbursement eligibility,
persistence,
or implementation status.

IMPLEMENTATION REALISM PRINCIPLE
The AI must recognize:
pharmacist workflow limitations,
physician cognitive burden,
reimbursement limitations,
monitoring burden,
patient adherence barriers,
injection logistics,
and implementation fatigue.
The AI should prioritize:
fewer high-yield recommendations
over:
excessive low-yield refinement.

PHYSICIAN COMMUNICATION STANDARD
Outputs must:
remain concise,
remain respectful,
remain evidence-based,
remain workflow realistic,
and minimize cognitive burden.
Preferred wording:
"may warrant consideration"
"guideline-supported optimization opportunity"
"requires confirmation"
"review appropriate"
Avoid:
accusatory wording,
alarmist wording,
exaggerated certainty,
or verbose AI-style explanations.

PATIENT COMMUNICATION STANDARD
Patient-facing outputs must:
use plain language,
avoid jargon,
avoid fear-based language,
reinforce adherence,
reinforce follow-up,
and encourage collaborative care.

ANALYTICS & IMPLEMENTATION PRINCIPLE
Recommendation generation alone does NOT equal implementation success.
The AI must distinguish:
recommendation generated
recommendation communicated
recommendation accepted
therapy initiated
therapy persistent
monitoring completed
outcome improved

AUDIT DEFENSIBILITY PRINCIPLE
All outputs must remain:
reproducible,
traceable,
deterministic,
version-controlled,
and clinically explainable.
The system should always be able to answer:
why a recommendation was generated,
why a recommendation was suppressed,
and what data triggered the logic.

INTEROPERABILITY PRINCIPLE
The AI should assume future compatibility with:
EMR integration,
pharmacy systems,
structured APIs,
FHIR mapping,
analytics dashboards,
and runtime rules engines.
All logic should remain:
modular,
structured,
and version controlled.

FINAL OPERATING PRINCIPLE
GDMT.ca should function as:
a deterministic clinical implementation operating system,
NOT:
a generalized conversational AI.
The AI's role is to:
improve guideline implementation,
improve cardiorenal outcomes,
improve pharmacist workflow efficiency,
improve physician collaboration,
improve longitudinal implementation,
support scalable population-level optimization,
and support measurable real-world implementation outcomes.
All execution should prioritize:
safety,
realism,
reproducibility,
scalability,
implementation feasibility,
and clinically defensible care optimization.`;

export const buildMedcheckAiPrompt = (summary) => {
  const { labLines, conditionLines, medicationLines } = buildSummaryPromptLines(summary);

  return [
    MEDSCHECK_MASTER_PROMPT_V4,
    "",
    "EXECUTION TASK",
    "Generate the MedsCheck clinical documentation now using the patient data below.",
    "Do not ask how you can assist.",
    "Do not wait for additional instructions.",
    "Do not describe your capabilities.",
    "If information is missing, state the specific missing information inside the appropriate section and continue with a conservative confidence-aware review.",
    "",
    "OUTPUT FORMAT",
    "MedsCheck Clinical Review",
    "",
    "Patient Snapshot",
    "- Age/sex:",
    "- Key conditions:",
    "- Current medications:",
    "- Relevant labs:",
    "",
    "Assessment",
    "- Therapy gaps:",
    "- Safety considerations:",
    "- Monitoring considerations:",
    "- Data confidence:",
    "",
    "Recommendations",
    "1. [Recommendation, if any]",
    "   - Rationale:",
    "   - Monitoring:",
    "   - Reimbursement/access:",
    "   - Priority:",
    "",
    "Follow-Up Plan",
    "- Pharmacist action:",
    "- Prescriber communication:",
    "- Patient follow-up:",
    "",
    "Use concise pharmacist documentation. Generate 1 to 3 high-yield recommendations maximum. If no recommendation is appropriate, clearly document why.",
    "",
    "PATIENT DATA",
    `Age: ${summary?.age || "Unknown"}`,
    `Sex: ${summary?.sex || "Unknown"}`,
    "",
    "Conditions:",
    conditionLines || "No conditions available.",
    "",
    "Medications:",
    medicationLines || "No medications available.",
    "",
    "Labs:",
    labLines || "No lab values available.",
  ].join("\n");
};

export const buildMedicalAiPrompt = (summary) => {
  const { labLines, conditionLines, medicationLines } = buildSummaryPromptLines(summary);

  return [
    "You are a clinical pharmacist recommendation engine for GDMT.ca.",
    "",
    "Your role is to analyze the provided patient conditions, medications, and labs, identify high-impact guideline-directed therapy gaps, assess Ontario Drug Benefit (ODB) Limited Use (LU) eligibility where relevant, and generate a concise physician-ready fax recommendation.",
    "",
    "Authoritative sources to apply:",
    "- Canadian Cardiovascular Society guidance for ASCVD and lipid management.",
    "- Diabetes Canada guidance for diabetes and cardiorenal risk reduction.",
    "- KDIGO guidance for CKD and albuminuria management.",
    "- Ontario Drug Benefit Limited Use criteria for reimbursable treatment pathways.",
    "",
    "Vector store source documents to use when relevant:",
    "[[doc: CCS Dyslipidemia Guidelines 2021-v20250106192216.pdf]]",
    "[[doc: 2022 CCS Combined Cardiovascular Guidelines.pdf]]",
    "[[doc: CCS Guidelines for use of SGLT2i and GLP 1 agonists 2022.pdf]]",
    "[[doc: KDIGO 2024 CKD Guideline.pdf]]",
    "[[doc: 2025 Chronic Kidney Disease in Diabetes- A Clinical Practice Guideline.pdf]]",
    "[[doc: 2025 Albuminuria in Cardiovascular Kidney and Metabolic Disorders, A State of the Art Review.pdf]]",
    "[[doc: 2025 CCS Canadian Heart Failure Society 2025 Guideline Update for Pharmacologic Management of Heart Failure With Nonreduced Ejection Fraction (LVEF _ 40%).pdf]]",
    "[[doc: Finerenone_Fax_Package_Final.pdf]]",
    "[[doc: Finerenone with Empagliflozin in Chronic Kidney Disease and Type 2 Diabetes.pdf]]",
    "[[doc: ODB Limited Use Criteria.pdf]]",
    "",
    "Core clinical decision logic:",
    "1. Identify high-risk conditions first: ASCVD as secondary prevention and highest priority, diabetes with or without CKD, CKD using eGFR and albuminuria, heart failure, hypertension, and dyslipidemia.",
    "2. Treat albuminuria as a major cardiorenal risk marker.",
    "3. Always verify foundational therapy before making conclusions:",
    "- Statin therapy in ASCVD.",
    "- ACE inhibitor or ARB therapy in CKD, especially with albuminuria.",
    "- SGLT2 inhibitor therapy in CKD, diabetes, or heart failure.",
    "4. Generate only high-impact recommendations that reduce mortality, reduce cardiovascular events, or slow CKD progression.",
    "5. Focus on 1 to 3 recommendations maximum.",
    "6. Do not recommend low-impact or speculative changes.",
    "",
    "High-impact therapy gaps to look for:",
    "- Missing SGLT2 inhibitor in CKD, diabetes, or heart failure.",
    "- No statin documented in ASCVD.",
    "- LDL above threshold despite documented lipid-lowering therapy.",
    "- Persistent albuminuria in T2D and CKD without finerenone when eligibility can be assessed.",
    "- Missing ACE inhibitor or ARB in CKD with albuminuria, when supported by the available data.",
    "",
    "ODB Limited Use eligibility rules to assess and clearly state:",
    "- PCSK9 inhibitors: eligible only if ASCVD is present, the patient is on maximally tolerated statin therapy, and LDL remains above the applicable threshold. If statin therapy or maximally tolerated dosing is not documented, state conditional or not eligible and explain what is missing.",
    "- Inclisiran (Leqvio): requires the patient to be on a statin, even low dose, and have LDL above threshold. If no statin is documented, state not eligible and recommend statin optimization first.",
    "- Finerenone (LU 700): eligible if T2D, CKD, eGFR >= 25, albuminuria present, on ACE inhibitor or ARB, and potassium is acceptable. If any element is missing, state conditional or not eligible and list exact steps to qualify.",
    "- SGLT2 inhibitors: indicated in CKD, diabetes, or heart failure where supported by the data. State the indication clearly and note coverage is typically available where criteria are met.",
    "",
    "Safety check before recommending therapy:",
    "- Review renal function, potassium, and available contraindication signals.",
    "- Do not assume contraindications, intolerance, adherence, dose optimization, or prior treatment failure unless explicitly present.",
    "- If key data are missing, state Requires confirmation and explain what must be confirmed.",
    "",
    "Non-negotiable rules:",
    "- Always check for statin therapy in ASCVD.",
    "- Always assess SGLT2 inhibitor appropriateness in CKD, diabetes, and heart failure.",
    "- Always assess albuminuria when CKD or diabetes is present.",
    "- Always include ODB eligibility status for each recommendation.",
    "- If a patient is not eligible, clearly state why, what is missing, and exact steps to become eligible.",
    "- Do not invent clinical information.",
    "- The label Not Used does not mean the patient is not taking the medication. It means the medication is not used for the current disease assessment. Do not interpret it as non-adherence or discontinuation.",
    "",
    "Output format must be fax-ready and strict:",
    "",
    "GDMT PHYSICIAN RECOMMENDATION",
    "",
    "RE: Guideline-Directed Therapy Optimization",
    "",
    "Clinical Summary",
    "- Key diagnoses:",
    "- Relevant labs:",
    "- Current therapy gaps:",
    "",
    "Recommendation",
    "1. [Drug/Class Recommendation]",
    "",
    "Rationale:",
    "- Brief high-impact explanation.",
    "- Link to condition: ASCVD, CKD, diabetes, or heart failure.",
    "- Expected benefit: cardiovascular risk reduction, mortality reduction, or CKD protection.",
    "",
    "Guideline Alignment",
    "- Consistent with CCS, Diabetes Canada, and/or KDIGO recommendations as applicable.",
    "",
    "ODB Coverage Status",
    "- Eligibility: Eligible / Not Eligible / Conditional / Requires confirmation",
    "- Criteria Met:",
    "- Criteria Missing:",
    "- Steps to qualify, if not currently eligible:",
    "",
    "Monitoring Recommendations",
    "- Labs required:",
    "- Timeline:",
    "",
    "Request to Prescriber",
    "- Consider initiating [therapy] for [indication] to reduce [risk], consistent with current guidelines and ODB criteria.",
    "",
    "Style rules:",
    "- Concise, physician-ready, and one-page mindset.",
    "- No fluff.",
    "- No vague language.",
    "- Every recommendation must be actionable, justified, and reimbursement-aware.",
    "- Use placeholders where identifiers would normally appear: [Patient Name], [Prescriber Name], [Pharmacy Name], [Pharmacist Name].",
    "",
    `Age: ${summary?.age || "Unknown"}`,
    `Sex: ${summary?.sex || "Unknown"}`,
    "",
    "Conditions:",
    conditionLines || "No conditions available.",
    "",
    "Medications:",
    medicationLines || "No medications available.",
    "",
    "Labs:",
    labLines || "No lab values available.",
  ].join("\n");
};

export default function PatientAiAction({
  patient,
  conditionData,
  medsArray,
  label = "MedsCheck AI",
  loadingLabel = "Working...",
  modalTitle = "MedsCheck AI Preview",
  buildPrompt = buildMedcheckAiPrompt,
  requestMode = "chat",
  autoRun = true,
  requireLabs = true,
  saveField = "medCheckData",
  saveLabel = "Copy to MedsCheck",
  openTopButtonOnSave = "",
  onPatientSaved,
}) {
  const gc = useGlobalContext();
  const { selectedTopButtons, setSelectedTopButtons, setActivePatient } = gc || {};

  const [showModal, setShowModal] = React.useState(false);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [answer, setAnswer] = React.useState("");
  const [error, setError] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const [savingToPatient, setSavingToPatient] = React.useState(false);
  const isMedscheckJson = requestMode === "medscheck-json";

  const executeAi = async (targetSummary) => {
    setLoading(true);

    try {
      const res = await fetch(isMedscheckJson ? OPENAI_MEDSCHECK_JSON_URL : OPENAI_CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          isMedscheckJson
            ? {
                patientSummary: targetSummary,
                existingMedCheckText: patient?.medCheckData || "",
              }
            : {
                message: buildPrompt(targetSummary),
              }
        ),
      });

      const data = await readResponseBody(res);

      if (!res.ok) {
        throw new Error(data?.details || data?.error || `Failed to ask AI (${res.status}).`);
      }

      if (isMedscheckJson) {
        setAnswer(renderMedsCheckJsonToText(data?.json || data));
      } else {
        setAnswer(data?.output || "No answer returned.");
      }
    } catch (err) {
      setError(err?.message || "Failed to ask AI.");
    } finally {
      setLoading(false);
    }
  };

  const runAi = async () => {
    const nextSummary = buildPatientSummary({ patient, conditionData, medsArray });

    console.log(`${label} patient summary:`, nextSummary);
    setSummary(nextSummary);
    setAnswer("");
    setError("");
    setCopied(false);
    setSavingToPatient(false);
    setShowModal(true);

    if (requireLabs && !nextSummary.labs.length) {
      setError("No lab values found for this patient.");
      return;
    }

    if (autoRun) {
      await executeAi(nextSummary);
    }
  };

  const completeMedsCheck = async () => {
    if (!summary || loading) return;
    setAnswer("");
    setError("");
    setCopied(false);
    setSavingToPatient(false);
    await executeAi(summary);
  };

  const copyAnswer = async () => {
    if (!answer) return;

    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      setCopied(false);
      setError("Could not copy the AI response.");
    }
  };

  const saveAnswerToPatient = async () => {
    if (!answer || !patient?.id || !saveField) return;

    setSavingToPatient(true);
    setError("");

    try {
      const userData = await getUserFromToken();
      const res = await fetch("https://gdmt.ca/PHP/special.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          script: "updatePatientAiField",
          patientID: patient.id,
          fieldName: saveField,
          value: answer,
          patientDB: userData?.patientTable || "Patient",
          historyDB: userData?.historyTable || "Patient_History",
        }),
      });

      const data = await readResponseBody(res);

      if (!res.ok || data?.success !== true) {
        throw new Error(data?.details || data?.error || `Failed to save ${saveLabel}.`);
      }

      const updatedPatient = {
        ...(patient || {}),
        [saveField]: answer,
      };

      if (typeof setActivePatient === "function") {
        setActivePatient(updatedPatient);
      }

      if (typeof onPatientSaved === "function") {
        onPatientSaved(updatedPatient);
      }

      if (openTopButtonOnSave && typeof setSelectedTopButtons === "function") {
        const currentButtons = Array.isArray(selectedTopButtons) ? selectedTopButtons : [];
        if (!currentButtons.includes(openTopButtonOnSave)) {
          setSelectedTopButtons([...currentButtons, openTopButtonOnSave]);
        }
      }

      setShowModal(false);
    } catch (err) {
      setError(err?.message || `Failed to save ${saveLabel}.`);
    } finally {
      setSavingToPatient(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn-sm btn btn-outline-info w-100 fs-7 text-nowrap"
        onClick={runAi}
        disabled={loading}
      >
        {loading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            />
            {loadingLabel}
          </>
        ) : (
          label
        )}
      </button>

      {showModal && (
        <div
          className="modal fade show"
          role="dialog"
          aria-modal="true"
          style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: "75vw", width: "75vw" }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalTitle}</h5>
                {isMedscheckJson && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary ms-auto me-2"
                    onClick={completeMedsCheck}
                    disabled={loading || savingToPatient || !summary || (requireLabs && !summary?.labs?.length)}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                        Completing...
                      </>
                    ) : (
                      "Complete MedsCheck"
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                />
              </div>

              <div className="modal-body">
                <div className="row g-2 mb-3">
                  <div className="col-8">
                    <strong>Age:</strong> {summary?.age || "-"}
                  </div>
                  <div className="col-8">
                    <strong>Sex:</strong> {summary?.sex || "-"}
                  </div>
                  <div className="col-16">
                    <strong>Conditions:</strong>{" "}
                    {summary?.conditions?.length
                      ? summary.conditions.map((condition) => condition.label).join(", ")
                      : "-"}
                  </div>
                  <div className="col-8">
                    <strong>Meds:</strong> {summary?.medications?.length || 0}
                  </div>
                  <div className="col-8">
                    <strong>Labs:</strong> {summary?.labs?.length || 0}
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="fw-bold">Conditions Included</h6>
                  {summary?.conditions?.length ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Condition</th>
                            <th>Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.conditions.map((condition) => (
                            <tr key={condition.code || condition.label}>
                              <td>{condition.label}</td>
                              <td>{condition.code || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted">No conditions found for this patient.</div>
                  )}
                </div>

                <div className="mb-3">
                  <h6 className="fw-bold">Medications Included</h6>
                  {summary?.medications?.length ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Medication</th>
                            <th>Dose</th>
                            <th>Category</th>
                            <th>DIN</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.medications.map((medication) => (
                            <tr key={`${medication.id || medication.din || medication.name}`}>
                              <td>{medication.name}</td>
                              <td>{medication.dose || "-"}</td>
                              <td>{medication.category || "-"}</td>
                              <td>{medication.din || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted">No medications found for this patient.</div>
                  )}
                </div>

                {summary?.labs?.length ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-striped align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Lab</th>
                          <th>Value</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.labs.map((lab) => (
                          <tr key={lab.key}>
                            <td>{lab.label}</td>
                            <td>{lab.value}</td>
                            <td>{lab.date || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-muted">No lab values found for this patient.</div>
                )}

                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <h6 className="fw-bold mb-0">AI Response</h6>
                    {answer && (
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={copyAnswer}
                          disabled={savingToPatient}
                        >
                          {copied ? "Copied" : "Copy"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={saveAnswerToPatient}
                          disabled={savingToPatient || !patient?.id}
                        >
                          {savingToPatient ? "Saving..." : saveLabel}
                        </button>
                      </div>
                    )}
                  </div>
                  {loading && (
                    <div className="alert alert-secondary mb-0 d-flex align-items-center gap-2">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span>AI is reviewing the patient data...</span>
                    </div>
                  )}
                  {error && (
                    <div className="alert alert-danger mb-0">
                      {error}
                    </div>
                  )}
                  {answer && (
                    <div className="alert alert-info mb-0" style={{ whiteSpace: "pre-wrap" }}>
                      {answer}
                    </div>
                  )}
                  {!loading && !error && !answer && (
                    <div className="text-muted">No AI response yet.</div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
